import React, { useState, useEffect } from 'react';
import NotificationBell from './NotificationBell';
import PMScheduleModal from './PMScheduleModal';
import CalendarModal from './CalendarModal';
import PMDateEditModal from './PMDateEditModal';

export default function InspectionPage({ currentUser }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [inspectionHistory, setInspectionHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDeleteInspectionModal, setShowDeleteInspectionModal] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // แก้ไขข้อมูลการตรวจสภาพ (เฉพาะ admin)
  const [showEditInspectionModal, setShowEditInspectionModal] = useState(false);
  const [inspectionToEdit, setInspectionToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    inspection_date: '',
    location: '',
    vehicle_number: '',
    technician_name: '',
    total_volt: '',
    v_diff: '',
    cycle_count: '',
    terminal_tightness: false,
    cleaning_done: false,
    terminal_cleaning: false,
    coulomb_check: false,
    charging_done: false,
    charger_status_display: false,
    charger_cooling_fan: false,
    charger_cable_condition: false,
    notes: '',
    status: ''
  });
  // Initial filter values
  const initialFilters = {
    saleDateFrom: '',
    saleDateTo: '',
    saleNumber: '',
    productName: '',
    serialNumber: '',
    customerName: '',
    statuses: [], // multi-select
    // removed: dueFrom, dueTo
  };

  // Form (draft) filters vs applied filters
  const [formFilters, setFormFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  // Expand status groups for filtering and API (e.g., 'scheduled' includes 'pending')
  const expandStatuses = (statusesArr = []) => {
    const out = new Set();
    (statusesArr || []).forEach(s => {
      if (s === 'scheduled') {
        out.add('scheduled');
        out.add('pending');
      } else {
        out.add(s);
      }
    });
    return Array.from(out);
  };
  const [newSalesCount, setNewSalesCount] = useState(0);
  const [newSalesData, setNewSalesData] = useState([]);
  const [showPMScheduleModal, setShowPMScheduleModal] = useState(false);
  const [selectedDeviceForPM, setSelectedDeviceForPM] = useState(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarInitialDate] = useState(null);
  const [calendarInitialView] = useState('list');
  const [calendarRefreshTrigger, setCalendarRefreshTrigger] = useState(0);
  const [showPMDateEditModal, setShowPMDateEditModal] = useState(false);
  const [pmDateEditData, setPmDateEditData] = useState(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'next_inspection_date', direction: 'asc' });

  // ดึงข้อมูลตารางงานตรวจสภาพ
  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (appliedFilters.serialNumber) params.set('serial', appliedFilters.serialNumber);
      if (appliedFilters.customerName) params.set('customer_name', appliedFilters.customerName);
      if (appliedFilters.saleNumber) params.set('sale_number', appliedFilters.saleNumber);
      if (appliedFilters.productName) params.set('product_name', appliedFilters.productName);
      if (appliedFilters.saleDateFrom) params.set('sale_date_from', appliedFilters.saleDateFrom);
      if (appliedFilters.saleDateTo) params.set('sale_date_to', appliedFilters.saleDateTo);
      // removed: due date filters
      if (appliedFilters.statuses && appliedFilters.statuses.length > 0) {
        const expanded = expandStatuses(appliedFilters.statuses);
        params.set('statuses', expanded.join(','));
      }
      const response = await fetch(`/api/pm-service/schedules${params.toString() ? `?${params.toString()}` : ''}`);
      if (!response.ok) {
        throw new Error('Failed to fetch schedules');
      }
      const data = await response.json();

      // ดึงข้อมูลจำนวนครั้งการตรวจสภาพสำหรับแต่ละ schedule
      const schedulesWithCount = await Promise.all(
        data.map(async (schedule) => {
          try {
            const historyResponse = await fetch(`/api/pm-service/inspections/history/${schedule.serial_number}`);
            if (historyResponse.ok) {
              const historyData = await historyResponse.json();
              return {
                ...schedule,
                inspection_count: historyData.inspection_count || 0,
                last_inspection_date: historyData.last_inspection_date
              };
            }
            return { ...schedule, inspection_count: 0, last_inspection_date: null };
          } catch (error) {
            console.error(`Error fetching history for ${schedule.serial_number}:`, error);
            return { ...schedule, inspection_count: 0, last_inspection_date: null };
          }
        })
      );

      setSchedules(schedulesWithCount);
      // ตรวจสอบวันที่ส่งมอบหลังโหลดข้อมูล
      validateDeliveryDates(schedulesWithCount);
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ฟังก์ชันสำหรับการลบประวัติการตรวจสภาพ (เฉพาะ admin)
  const handleDeleteInspection = (inspection) => {
    setInspectionToDelete(inspection);
    setShowDeleteInspectionModal(true);
  };

  const confirmDeleteInspection = async () => {
    if (!inspectionToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await fetch(`/api/pm-service/inspections/${inspectionToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // รีเฟรชข้อมูลประวัติการตรวจสภาพ
        if (inspectionHistory && inspectionHistory.serial_number) {
          await handleOpenHistoryModal(inspectionHistory.serial_number);
        }
        setShowDeleteInspectionModal(false);
        setInspectionToDelete(null);
        alert('ลบประวัติการตรวจสภาพเรียบร้อยแล้ว');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'ไม่สามารถลบข้อมูลได้');
      }
    } catch (error) {
      console.error('Error deleting inspection:', error);
      alert('เกิดข้อผิดพลาดในการลบข้อมูล: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // เปิดโมดอลแก้ไขประวัติการตรวจสภาพ
  const handleOpenEditInspection = (inspection) => {
    setInspectionToEdit(inspection);
    setEditForm({
      inspection_date: inspection.inspection_date ? new Date(inspection.inspection_date).toISOString().split('T')[0] : '',
      location: inspection.location || '',
      vehicle_number: inspection.vehicle_number || '',
      technician_name: inspection.technician_name || '',
      total_volt: inspection.total_volt ?? '',
      v_diff: inspection.v_diff ?? '',
      cycle_count: inspection.cycle_count ?? '',
      terminal_tightness: !!inspection.terminal_tightness,
      cleaning_done: !!inspection.cleaning_done,
      terminal_cleaning: !!inspection.terminal_cleaning,
      coulomb_check: !!inspection.coulomb_check,
      charging_done: !!inspection.charging_done,
      charger_status_display: !!inspection.charger_status_display,
      charger_cooling_fan: !!inspection.charger_cooling_fan,
      charger_cable_condition: !!inspection.charger_cable_condition,
      customer_signature: !!inspection.customer_signature,
      notes: inspection.notes || '',
      status: inspection.status || ''
    });
    setShowEditInspectionModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateInspection = async () => {
    if (!inspectionToEdit) return;
    try {
      setDeleteLoading(true);
      const payload = {
        inspection_date: editForm.inspection_date || null,
        location: editForm.location || null,
        vehicle_number: editForm.vehicle_number || null,
        technician_name: editForm.technician_name || null,
        total_volt: editForm.total_volt !== '' ? Number(editForm.total_volt) : null,
        v_diff: editForm.v_diff !== '' ? Number(editForm.v_diff) : null,
        cycle_count: editForm.cycle_count !== '' ? Number(editForm.cycle_count) : null,
        terminal_tightness: editForm.terminal_tightness,
        cleaning_done: editForm.cleaning_done,
        terminal_cleaning: editForm.terminal_cleaning,
        coulomb_check: editForm.coulomb_check,
        charging_done: editForm.charging_done,
        charger_status_display: editForm.charger_status_display,
        charger_cooling_fan: editForm.charger_cooling_fan,
        charger_cable_condition: editForm.charger_cable_condition,
        customer_signature: editForm.customer_signature,
        notes: editForm.notes || null,
        status: editForm.status || null
      };

      const response = await fetch(`/api/pm-service/inspections/${inspectionToEdit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'อัปเดตข้อมูลไม่สำเร็จ');
      }

      // รีโหลดประวัติสำหรับซีเรียลนี้
      if (inspectionHistory && inspectionHistory.serial_number) {
        await handleOpenHistoryModal(inspectionHistory.serial_number);
      }
      setShowEditInspectionModal(false);
      setInspectionToEdit(null);
      alert('อัปเดตข้อมูลการตรวจสภาพเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error updating inspection:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ฟังก์ชันดึงข้อมูลรายการขายใหม่ที่ยังไม่ได้ sync
  const fetchNewSales = async () => {
    try {
      const response = await fetch('/api/sales/new-unsync');
      if (response.ok) {
        const data = await response.json();
        setNewSalesCount(data.count);
        setNewSalesData(data.sales);
      } else {
        console.error('Failed to fetch new sales data');
      }
    } catch (error) {
      console.error('Error fetching new sales:', error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchSchedules();
    fetchNewSales();

    // ตั้งเวลาให้ตรวจสอบรายการใหม่ทุก 30 วินาที
    const interval = setInterval(fetchNewSales, 30000);

    return () => clearInterval(interval);
  }, [
    appliedFilters.serialNumber,
    appliedFilters.customerName,
    appliedFilters.saleNumber,
    appliedFilters.productName,
    appliedFilters.saleDateFrom,
    appliedFilters.saleDateTo,
    // removed dueFrom/dueTo from dependencies
    // stringify statuses array to watch changes
    JSON.stringify(appliedFilters.statuses)
  ]);

  // ฟังก์ชัน sync ข้อมูลการขายใหม่เข้าสู่ระบบ PM
  const handleSyncSales = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/pm-service/schedules/sync-sales', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to sync sales data');
      }

      const result = await response.json();
      alert(`Sync สำเร็จ: เพิ่มข้อมูล ${result.newSchedules || 0} รายการ`);

      // Refresh schedules data และข้อมูลรายการใหม่
      await fetchSchedules();
      await fetchNewSales();
      // Refresh calendar data
      setCalendarRefreshTrigger(prev => prev + 1);
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการ sync ข้อมูล: ' + err.message);
      alert('เกิดข้อผิดพลาดในการ sync ข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  // ฟังก์ชันจัดการการเปลี่ยนแปลงการค้นหา
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setFormFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleStatusToggle = (statusKey) => {
    setFormFilters(prev => {
      const set = new Set(prev.statuses);
      if (set.has(statusKey)) {
        set.delete(statusKey);
      } else {
        set.add(statusKey);
      }
      return { ...prev, statuses: Array.from(set) };
    });
  };

  const applySearch = () => {
    setAppliedFilters({ ...formFilters });
  };

  const clearSearch = () => {
    setFormFilters(initialFilters);
    setAppliedFilters(initialFilters);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // กรองข้อมูลตามเงื่อนไขการค้นหา
  const filteredSchedules = schedules.filter(schedule => {
    // Text matches (case-insensitive)
    const matchSerial = (schedule.serial_number || '').toLowerCase().includes((appliedFilters.serialNumber || '').toLowerCase());
    const custName = (schedule.customer_name || schedule.pm_customer_name || schedule.customer_name_from_sale || '');
    const matchCustomer = custName.toLowerCase().includes((appliedFilters.customerName || '').toLowerCase());
    const saleNumStr = String(schedule.sale_number || '').toLowerCase();
    const matchSaleNumber = saleNumStr.includes((appliedFilters.saleNumber || '').toLowerCase());
    const productDisplay = (schedule.display_product_name || schedule.product_name || schedule.display_product_type || '');
    const matchProduct = productDisplay.toLowerCase().includes((appliedFilters.productName || '').toLowerCase());

    // Status
    const effectiveStatus = schedule.overall_status || schedule.status;
    let matchStatus = true;
    if (appliedFilters.statuses && appliedFilters.statuses.length > 0) {
      const allowed = new Set(expandStatuses(appliedFilters.statuses));
      matchStatus = allowed.has(effectiveStatus);
    } else {
      // default: hide completed when no status selected
      matchStatus = effectiveStatus !== 'completed';
    }

    // Sale date range (parse แบบ strict เพื่อหลีกเลี่ยง timezone shift)
    let matchSaleDate = true;
    if (appliedFilters.saleDateFrom || appliedFilters.saleDateTo) {
      const saleDateStr = schedule.sale_date || schedule.saleDate;
      const saleDate = saleDateStr ? parseDateStrict(saleDateStr) : null;
      if (appliedFilters.saleDateFrom) {
        const from = parseDateStrict(appliedFilters.saleDateFrom);
        matchSaleDate = matchSaleDate && saleDate && from && saleDate >= from;
      }
      if (appliedFilters.saleDateTo) {
        const to = parseDateStrict(appliedFilters.saleDateTo);
        matchSaleDate = matchSaleDate && saleDate && to && saleDate <= to;
      }
    }

    return matchSerial && matchCustomer && matchSaleNumber && matchProduct && matchStatus && matchSaleDate;
  });

  // Sort the filtered results
  const sortedSchedules = [...filteredSchedules].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle specific fields
    if (sortConfig.key === 'product_name') {
      aValue = a.product_name || a.display_product_name || a.item_product_type || '';
      bValue = b.product_name || b.display_product_name || b.item_product_type || '';
    } else if (sortConfig.key === 'customer_name') {
      aValue = a.customer_name || a.pm_customer_name || a.customer_name_from_sale || '';
      bValue = b.customer_name || b.pm_customer_name || b.customer_name_from_sale || '';
    } else if (sortConfig.key === 'customer_phone') {
      aValue = a.customer_phone || a.pm_customer_phone || a.customer_phone_from_customers || '';
      bValue = b.customer_phone || b.pm_customer_phone || b.customer_phone_from_customers || '';
    }

    // Handle null/undefined
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    // String comparison
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toString().toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // ฟังก์ชันแสดงสถานะ
  const getStatusBadge = (status, inspectionCount = 0, totalRequired = 0, overallStatus = null) => {
    const effective = overallStatus || status;
    const statusConfig = {
      'pending': { class: 'bg-warning', text: 'กำหนดการ' },
      'scheduled': { class: 'bg-warning', text: 'กำหนดการ' },
      'in_progress': { class: 'bg-info', text: 'กำลังดำเนินการ' },
      'completed': { class: 'bg-success', text: 'เสร็จสิ้น' },
      'cancelled': { class: 'bg-danger', text: 'ยกเลิก' }
    };
    const config = statusConfig[effective] || { class: 'bg-secondary', text: effective };
    const suffix = totalRequired > 0 ? ` (${inspectionCount}/${totalRequired} ครั้ง)` : (inspectionCount > 0 ? ` (${inspectionCount} ครั้ง)` : '');
    const tooltip = totalRequired > 0
      ? `รวมทั้งหมด ${totalRequired} ครั้ง ต้องทำให้ครบจึงเสร็จสิ้น\nดำเนินการแล้ว ${inspectionCount} ครั้ง`
      : 'ยังไม่กำหนดจำนวนครั้งทั้งหมด';
    return <span className={`badge ${config.class}`} title={tooltip}>{config.text}{suffix}</span>;
  };

  // ฟังก์ชันช่วย parse วันที่แบบปลอดภัย (ป้องกัน timezone shift จากรูปแบบ YYYY-MM-DD)
  const parseDateStrict = (dateString) => {
    if (!dateString || dateString === 'null' || dateString === 'undefined') {
      return null;
    }
    if (typeof dateString === 'string') {
      const clean = dateString.includes('T') ? dateString.split('T')[0] : dateString;
      const m = clean.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
      if (m) {
        const y = Number(m[1]);
        const mIdx = Number(m[2]) - 1;
        const d = Number(m[3]);
        const local = new Date(y, mIdx, d);
        return isNaN(local.getTime()) ? null : local;
      }
    }
    const fallback = new Date(dateString);
    return isNaN(fallback.getTime()) ? null : fallback;
  };

  // ฟังก์ชันจัดรูปแบบวันที่ (ใช้ parse แบบ strict)
  const formatDate = (dateString) => {
    const date = parseDateStrict(dateString);
    if (!date) return 'ไม่ระบุวันที่';
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ตรวจสอบความถูกต้องของวันที่ส่งมอบในข้อมูล schedules หลังการโหลด/หลัง sync
  const validateDeliveryDates = (list = []) => {
    let invalidCount = 0;
    for (const s of list) {
      if (s && s.delivery_date) {
        const d = parseDateStrict(s.delivery_date);
        if (!d) invalidCount++;
      }
    }
    if (invalidCount > 0) {
      console.warn(`พบวันที่ส่งมอบที่ไม่ถูกต้อง ${invalidCount} รายการในตารางตรวจสภาพ`);
      try { alert(`พบวันที่ส่งมอบที่ไม่ถูกต้อง ${invalidCount} รายการ กรุณาตรวจสอบข้อมูลการขาย`); } catch { }
    }
  };

  // ฟังก์ชันเปิด Modal ตรวจสภาพ
  const handleOpenInspectionModal = (schedule) => {
    setSelectedSchedule(schedule);
    setShowInspectionModal(true);
  };

  // ฟังก์ชันปิด Modal ตรวจสภาพ
  const handleCloseInspectionModal = () => {
    setShowInspectionModal(false);
    setSelectedSchedule(null);
  };

  // ฟังก์ชันจัดการการส่งข้อมูลการตรวจสภาพ
  const handleInspectionSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData(e.target);
      const inspectionData = {
        schedule_id: selectedSchedule.id,
        serial_number: selectedSchedule.serial_number,
        inspection_date: formData.get('inspection_date'),
        location: formData.get('inspection_location'),
        vehicle_number: formData.get('vehicle_registration'),
        technician_name: currentUser ? currentUser.name : 'ไม่ระบุ',
        total_volt: parseFloat(formData.get('total_voltage')) || 0,
        v_diff: parseFloat(formData.get('voltage_difference')) || 0,
        cycle_count: parseInt(formData.get('charge_cycles')) || 0,
        terminal_tightness: formData.get('terminal_tightness') === 'on',
        cleaning_done: formData.get('cleaning') === 'on',
        terminal_cleaning: formData.get('terminal_cleaning') === 'on',
        coulomb_check: formData.get('coulomb') === 'on',
        charging_done: formData.get('charging') === 'on',
        charger_status_display: formData.get('charger_status_display') === 'on',
        charger_cooling_fan: formData.get('charger_cooling_fan') === 'on',
        charger_cable_condition: formData.get('charger_cable_condition') === 'on',
        status: formData.get('status'),
        notes: formData.get('notes'),
        customer_signature: formData.get('customerSignature') === 'on'
      };

      const response = await fetch('/api/pm-service/inspections', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inspectionData),
      });

      if (response.ok) {
        const result = await response.json();
        alert('บันทึกข้อมูลการตรวจสภาพเรียบร้อยแล้ว และสร้าง PM Schedule ครั้งถัดไปแล้ว');
        handleCloseInspectionModal();
        // Refresh schedules data
        fetchSchedules();
        // Refresh calendar data if calendar modal is open
        setCalendarRefreshTrigger(prev => prev + 1);
        // ไม่ต้องเปิด modal แก้ไขวันที่ เนื่องจากระบบคำนวณอัตโนมัติแล้ว
      } else {
        const error = await response.json();
        alert('เกิดข้อผิดพลาด: ' + error.error);
      }
    } catch (error) {
      console.error('Error submitting inspection:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  // ฟังก์ชันแสดง Modal เลือกภาษา PDF
  const [showPDFLanguageModal, setShowPDFLanguageModal] = useState(false);
  const [selectedSerialForPDF, setSelectedSerialForPDF] = useState(null);
  const [selectedInspectionForPDF, setSelectedInspectionForPDF] = useState(null);

  const handleOpenPDFLanguageModal = (serialNumber) => {
    setSelectedSerialForPDF(serialNumber);
    setSelectedInspectionForPDF(null); // Reset inspection ID for latest
    setShowPDFLanguageModal(true);
  };

  // ฟังก์ชันเปิด Modal PDF สำหรับการตรวจสภาพเฉพาะครั้ง
  const handleOpenSpecificInspectionPDF = (inspection) => {
    setSelectedSerialForPDF(inspection.serial_number);
    setSelectedInspectionForPDF(inspection);
    setShowPDFLanguageModal(true);
  };

  // ฟังก์ชันดาวน์โหลด PDF - ใช้ serial number ในการสร้าง PDF
  const handleDownloadPDF = async (serialNumber, language = 'en') => {
    try {
      let inspectionId;
      let inspectionToDownload;

      // ถ้ามีการเลือกการตรวจสภาพเฉพาะครั้ง ให้ใช้ข้อมูลนั้น
      if (selectedInspectionForPDF && selectedSerialForPDF === serialNumber) {
        inspectionToDownload = selectedInspectionForPDF;
        inspectionId = selectedInspectionForPDF.id;
      } else {
        // ถ้าไม่ได้เลือกเฉพาะครั้ง ให้ดึงข้อมูลการตรวจสภาพล่าสุด
        const historyResponse = await fetch(`/api/pm-service/inspections/history/${serialNumber}`);
        if (!historyResponse.ok) {
          throw new Error('ไม่สามารถหาข้อมูลการตรวจสภาพได้');
        }

        const historyData = await historyResponse.json();
        if (!historyData.inspections || historyData.inspections.length === 0) {
          alert('ไม่พบข้อมูลการตรวจสภาพสำหรับซีเรียลนัมเบอร์นี้');
          return;
        }

        // Use the most recent inspection (first in array)
        inspectionToDownload = historyData.inspections[0];
        inspectionId = inspectionToDownload.id;
      }

      console.log('Downloading PDF for inspection ID:', inspectionId, 'Language:', language);

      const response = await fetch(`/api/pm-service/inspections/${inspectionId}/pdf?lang=${language}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `inspection-report-${serialNumber}-${inspectionId}-${language}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        // ปิด Modal หลังดาวน์โหลดเสร็จ
        setShowPDFLanguageModal(false);
        setSelectedSerialForPDF(null);
        setSelectedInspectionForPDF(null);
      } else {
        const errorText = await response.text();
        console.error('PDF download error:', errorText);
        alert('เกิดข้อผิดพลาดในการดาวน์โหลด PDF: ' + errorText);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('เกิดข้อผิดพลาดในการดาวน์โหลด PDF: ' + error.message);
    }
  };

  // ฟังก์ชันเปิด Modal ประวัติการตรวจสภาพ
  const handleOpenHistoryModal = async (serialNumber) => {
    try {
      setHistoryLoading(true);
      setShowHistoryModal(true);

      const response = await fetch(`/api/pm-service/inspections/history/${serialNumber}`);
      if (response.ok) {
        const data = await response.json();
        setInspectionHistory(data);
      } else {
        const error = await response.json();
        alert('เกิดข้อผิดพลาด: ' + error.error);
        setShowHistoryModal(false);
      }
    } catch (error) {
      console.error('Error fetching inspection history:', error);
      alert('เกิดข้อผิดพลาดในการโหลดประวัติการตรวจสภาพ');
      setShowHistoryModal(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ฟังก์ชันปิด Modal ประวัติการตรวจสภาพ
  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setInspectionHistory(null);
  };

  const handleDeleteSchedule = (schedule) => {
    setScheduleToDelete(schedule);
    setShowDeleteModal(true);
  };

  // บันทึกการแก้ไขวันที่ PM จาก PMDateEditModal
  const handleSavePMDateFromModal = async (updateData) => {
    try {
      const response = await fetch(`/api/pm-service/schedules/${updateData.pm_schedule_id}/update-date`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          next_inspection_date: updateData.new_date,
          last_service_date: updateData.last_service_date,
          calculated_date: updateData.calculated_date,
          use_calculated: updateData.use_calculated,
          notes: updateData.notes,
          updated_by: 1
        }),
      });

      if (response.ok) {
        await fetchSchedules();
        setCalendarRefreshTrigger(prev => prev + 1); // Refresh calendar
        alert('อัปเดตวันที่กำหนด PM สำเร็จ');
        setShowPMDateEditModal(false);
        setPmDateEditData(null);
      } else {
        const error = await response.json();
        alert(`เกิดข้อผิดพลาด: ${error.error || 'ไม่สามารถอัปเดตวันที่ได้'}`);
        throw new Error(error.error || 'เกิดข้อผิดพลาดในการอัปเดต');
      }
    } catch (error) {
      console.error('Error updating PM date:', error);
      if (!error.message.includes('เกิดข้อผิดพลาด:')) {
        alert('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
      }
      throw error;
    }
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/pm-service/schedules/${scheduleToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // รีเฟรชข้อมูลหลังจากลบสำเร็จ
        await fetchSchedules();
        setShowDeleteModal(false);
        setScheduleToDelete(null);
        alert('ลบข้อมูลงานตรวจสภาพเรียบร้อยแล้ว');
      } else {
        throw new Error('ไม่สามารถลบข้อมูลได้');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('เกิดข้อผิดพลาดในการลบข้อมูล: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteSchedule = () => {
    setShowDeleteModal(false);
    setScheduleToDelete(null);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 thai-text">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="thai-text">
          <i className="fas fa-clipboard-check me-2"></i>
          ตรวจสภาพ
        </h3>
        <div className="d-flex align-items-center gap-3">
          {currentUser && (
            <div className="bg-light rounded-pill px-3 py-2 border">
              <small className="text-muted">
                <i className="fas fa-user me-2"></i>
                <strong>{currentUser.name}</strong>
                {currentUser.position && (
                  <span className="ms-2 text-primary">
                    <i className="fas fa-user-tag me-1"></i>
                    {currentUser.position}
                  </span>
                )}
              </small>
            </div>
          )}
          <NotificationBell
            newSalesCount={newSalesCount}
            newSalesData={newSalesData}
            onSyncSales={handleSyncSales}
            loading={loading}
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <span className="thai-text">{error}</span>
        </div>
      )}

      {/* Modal แก้ไขประวัติการตรวจสภาพ (เฉพาะแอดมิน) */}
      {showEditInspectionModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title thai-text">
                  <i className="fas fa-edit text-primary me-2"></i>
                  แก้ไขประวัติการตรวจสภาพ
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowEditInspectionModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* โครงแบบการ์ดให้เหมือนฟอร์มตรวจสภาพ */}
                <div className="row">
                  {/* ข้อมูลการตรวจสภาพ */}
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0 thai-text">ข้อมูลการตรวจสภาพ</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label thai-text">วันที่ตรวจสภาพ</label>
                          <input type="date" className="form-control" name="inspection_date" value={editForm.inspection_date || ''} onChange={handleEditChange} />
                        </div>
                        <div className="mb-3">
                          <label className="form-label thai-text">สถานที่ตรวจสภาพ</label>
                          <input type="text" className="form-control" name="location" value={editForm.location || ''} onChange={handleEditChange} />
                        </div>
                        <div className="mb-3">
                          <label className="form-label thai-text">หมายเลขรถ</label>
                          <input type="text" className="form-control" name="vehicle_number" value={editForm.vehicle_number || ''} onChange={handleEditChange} />
                        </div>
                        <div className="mb-3">
                          <label className="form-label thai-text">ชื่อช่างเทคนิค</label>
                          <input type="text" className="form-control" name="technician_name" value={editForm.technician_name || (currentUser ? currentUser.name : '')} onChange={handleEditChange} readOnly style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }} />
                          <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            ชื่อช่างเทคนิคจะถูกดึงจากผู้ใช้ที่เข้าสู่ระบบ
                            {currentUser && currentUser.position && (
                              <span className="ms-2">
                                <i className="fas fa-user-tag me-1"></i>
                                ตำแหน่ง: {currentUser.position}
                              </span>
                            )}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ข้อมูลทางเทคนิค */}
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0 thai-text">ข้อมูลทางเทคนิค</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label thai-text">Total Volt (50-55V)</label>
                          <input type="number" step="0.01" className="form-control" name="total_volt" value={editForm.total_volt ?? ''} onChange={handleEditChange} />
                        </div>
                        <div className="mb-3">
                          <label className="form-label thai-text">Volt Diff (~0.1)</label>
                          <input type="number" step="0.001" className="form-control" name="v_diff" value={editForm.v_diff ?? ''} onChange={handleEditChange} />
                        </div>
                        <div className="mb-3">
                          <label className="form-label thai-text">Cycle Count (~300)</label>
                          <input type="number" className="form-control" name="cycle_count" value={editForm.cycle_count ?? ''} onChange={handleEditChange} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* งานบำรุงรักษา */}
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0 thai-text">งานบำรุงรักษา</h6>
                      </div>
                      <div className="card-body">
                        {/* แบตเตอรี่ */}
                        <div className="mb-4">
                          <h6 className="text-primary mb-3 thai-text">
                            <i className="fas fa-battery-three-quarters me-2"></i>
                            แบตเตอรี่
                          </h6>
                          <div className="row">
                            <div className="col-md-3">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="cleaning_done" name="cleaning_done" checked={!!editForm.cleaning_done} onChange={handleEditChange} />
                                <label className="form-check-label thai-text" htmlFor="cleaning_done">ทำความสะอาดภายนอก</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="terminal_cleaning" name="terminal_cleaning" checked={!!editForm.terminal_cleaning} onChange={handleEditChange} />
                                <label className="form-check-label thai-text" htmlFor="terminal_cleaning">ทำความสะอาดเทอมินอล</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="terminal_tightness" name="terminal_tightness" checked={!!editForm.terminal_tightness} onChange={handleEditChange} />
                                <label className="form-check-label thai-text" htmlFor="terminal_tightness">ขันแน่นเทอมินอล (10Nm)</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="coulomb_check" name="coulomb_check" checked={!!editForm.coulomb_check} onChange={handleEditChange} />
                                <label className="form-check-label thai-text" htmlFor="coulomb_check">การแสดงผลคูลอมป์</label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ชาร์จเจอร์ */}
                        <div className="mb-2">
                          <h6 className="text-success mb-3 thai-text">
                            <i className="fas fa-plug me-2"></i>
                            ชาร์จเจอร์
                          </h6>
                          <div className="row">
                            <div className="col-md-3">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="charging_done" name="charging_done" checked={!!editForm.charging_done} onChange={handleEditChange} />
                                <label className="form-check-label thai-text" htmlFor="charging_done">ชาร์จเข้า (14-15A)</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="charger_status_display" name="charger_status_display" checked={!!editForm.charger_status_display} onChange={handleEditChange} />
                                <label className="form-check-label thai-text" htmlFor="charger_status_display">ไฟแสดงสถานะ</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="charger_cooling_fan" name="charger_cooling_fan" checked={!!editForm.charger_cooling_fan} onChange={handleEditChange} />
                                <label className="form-check-label thai-text" htmlFor="charger_cooling_fan">พัดลมระบายความร้อน</label>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="charger_cable_condition" name="charger_cable_condition" checked={!!editForm.charger_cable_condition} onChange={handleEditChange} />
                                <label className="form-check-label thai-text" htmlFor="charger_cable_condition">สายไฟไม่ชำรุด</label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* หมายเหตุ และสถานะ/ลายเซ็น */}
                <div className="row mt-3">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label thai-text">หมายเหตุ</label>
                      <textarea className="form-control" rows="4" name="notes" value={editForm.notes || ''} onChange={handleEditChange} placeholder="ระบุหมายเหตุเพิ่มเติม..."></textarea>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label thai-text">สถานะการตรวจสภาพ</label>
                      <select className="form-select" name="status" value={editForm.status || ''} onChange={handleEditChange}>
                        <option value="">ไม่ระบุ</option>
                        <option value="scheduled">กำหนดการ</option>
                        <option value="in_progress">กำลังดำเนินการ</option>
                        <option value="completed">เสร็จสิ้น</option>
                        <option value="cancelled">ยกเลิก</option>
                      </select>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="customer_signature" name="customer_signature" checked={!!editForm.customer_signature} onChange={handleEditChange} />
                      <label className="form-check-label thai-text" htmlFor="customer_signature">ลูกค้าลงลายเซ็นแล้ว</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditInspectionModal(false)}>
                  <i className="fas fa-times me-2"></i>
                  <span className="thai-text">ยกเลิก</span>
                </button>
                <button type="button" className="btn btn-primary" onClick={handleUpdateInspection} disabled={deleteLoading}>
                  {deleteLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      <span className="thai-text">กำลังบันทึก...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      <span className="thai-text">บันทึกการแก้ไข</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ฟอร์มค้นหา - ปรับดีไซน์และเงื่อนไขให้ครอบคลุม (คลิกปุ่มค้นหาเพื่อใช้ตัวกรอง) */}
      <div className="card mb-4 shadow-sm border-0">
        <div className="card-header text-white" style={{
          background: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)'
        }}>
          <h6 className="mb-0 thai-text d-flex align-items-center">
            <i className="fas fa-search me-2"></i>
            ค้นหาและกรองข้อมูล
          </h6>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* แถวที่ 1: Sale Number, Product, Serial, Customer */}
            <div className="col-md-3">
              <label className="form-label thai-text">หมายเลขการขาย</label>
              <div className="input-group">
                <span className="input-group-text"><i className="fas fa-hashtag"></i></span>
                <input type="text" className="form-control" name="saleNumber" placeholder="ค้นหาหมายเลขการขาย..." value={formFilters.saleNumber} onChange={handleSearchChange} />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label thai-text">รายการสินค้า</label>
              <div className="input-group">
                <span className="input-group-text"><i className="fas fa-box"></i></span>
                <input type="text" className="form-control" name="productName" placeholder="ค้นหารายการสินค้า..." value={formFilters.productName} onChange={handleSearchChange} />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label thai-text">หมายเลขซีเรียล</label>
              <div className="input-group">
                <span className="input-group-text"><i className="fas fa-barcode"></i></span>
                <input type="text" className="form-control" name="serialNumber" placeholder="ค้นหาหมายเลขซีเรียล..." value={formFilters.serialNumber} onChange={handleSearchChange} />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label thai-text">ชื่อลูกค้า</label>
              <div className="input-group">
                <span className="input-group-text"><i className="fas fa-user"></i></span>
                <input type="text" className="form-control" name="customerName" placeholder="ค้นหาชื่อลูกค้า..." value={formFilters.customerName} onChange={handleSearchChange} />
              </div>
            </div>

            {/* แถวที่ 2: สถานะ (multi-select) + Sale date range */}
            <div className="col-md-6">
              <label className="form-label thai-text d-block">สถานะ (เลือกได้หลายค่า)</label>
              <div className="d-flex flex-wrap gap-2">
                {[
                  { key: 'scheduled', label: 'กำหนดการ', color: 'btn-outline-warning' },
                  { key: 'in_progress', label: 'กำลังดำเนินการ', color: 'btn-outline-info' },
                  { key: 'completed', label: 'เสร็จสิ้น', color: 'btn-outline-success' },
                  { key: 'cancelled', label: 'ยกเลิก', color: 'btn-outline-danger' },
                ].map(s => (
                  <button
                    key={s.key}
                    type="button"
                    className={`btn btn-sm ${formFilters.statuses.includes(s.key) ? s.color.replace('outline-', '') : s.color}`}
                    onClick={() => handleStatusToggle(s.key)}
                    aria-pressed={formFilters.statuses.includes(s.key)}
                  >
                    <i className="fas fa-check me-1" style={{ visibility: formFilters.statuses.includes(s.key) ? 'visible' : 'hidden' }}></i>
                    <span className="thai-text">{s.label}</span>
                  </button>
                ))}
              </div>
              <small className="text-muted thai-text d-block mt-1">ถ้าไม่เลือกสถานะใด ระบบจะซ่อนรายการ "เสร็จสิ้น" อัตโนมัติ</small>
            </div>
            <div className="col-md-3">
              <label className="form-label thai-text">วันที่ปิดการขาย (เริ่ม)</label>
              <div className="input-group">
                <span className="input-group-text"><i className="far fa-calendar"></i></span>
                <input type="date" className="form-control" name="saleDateFrom" value={formFilters.saleDateFrom} onChange={handleSearchChange} />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label thai-text">วันที่ปิดการขาย (สิ้นสุด)</label>
              <div className="input-group">
                <span className="input-group-text"><i className="far fa-calendar"></i></span>
                <input type="date" className="form-control" name="saleDateTo" value={formFilters.saleDateTo} onChange={handleSearchChange} />
              </div>
            </div>

            {/* แถวที่ 3: (removed due date filters as requested) */}
          </div>

          <div className="d-flex gap-2 mt-3">
            <button type="button" className="btn btn-primary" onClick={applySearch}>
              <i className="fas fa-search me-1"></i>
              <span className="thai-text">ค้นหา</span>
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={clearSearch}>
              <i className="fas fa-times me-1"></i>
              <span className="thai-text">ล้างการค้นหา</span>
            </button>
          </div>
        </div>
      </div>

      {/* ตารางแสดงข้อมูล */}
      <div className="card">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0 thai-text">รายการงานตรวจสภาพ ({sortedSchedules.length} รายการ)</h5>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-info"
              onClick={() => setShowCalendarModal(true)}
              title="ดูปฏิทินวันที่กำหนด"
            >
              <i className="fas fa-calendar-alt me-1"></i>
              <span className="thai-text">ปฏิทิน</span>
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSyncSales}
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-1"></i>
              <span className="thai-text">{loading ? 'กำลัง Sync...' : 'Sync ข้อมูลการขาย'}</span>
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th className="thai-text" onClick={() => handleSort('delivery_date')} style={{ cursor: 'pointer' }}>
                    วันที่ส่งมอบ {sortConfig.key === 'delivery_date' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text" onClick={() => handleSort('sale_number')} style={{ cursor: 'pointer' }}>
                    หมายเลขการขาย {sortConfig.key === 'sale_number' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text" onClick={() => handleSort('product_name')} style={{ cursor: 'pointer' }}>
                    รายการสินค้า {sortConfig.key === 'product_name' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text" onClick={() => handleSort('serial_number')} style={{ cursor: 'pointer' }}>
                    หมายเลขซีเรียล {sortConfig.key === 'serial_number' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text" onClick={() => handleSort('customer_name')} style={{ cursor: 'pointer' }}>
                    ชื่อลูกค้า {sortConfig.key === 'customer_name' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text" onClick={() => handleSort('customer_phone')} style={{ cursor: 'pointer' }}>
                    เบอร์โทร {sortConfig.key === 'customer_phone' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text" onClick={() => handleSort('last_inspection_date')} style={{ cursor: 'pointer' }}>
                    ดำเนินการล่าสุด {sortConfig.key === 'last_inspection_date' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text" onClick={() => handleSort('next_inspection_date')} style={{ cursor: 'pointer' }}>
                    วันที่กำหนด {sortConfig.key === 'next_inspection_date' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text" onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    สถานะ {sortConfig.key === 'status' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>}
                  </th>
                  <th className="thai-text">การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {sortedSchedules.length > 0 ? (
                  sortedSchedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td>
                        {(() => {
                          const d = schedule.delivery_date || null;
                          if (d) return formatDate(d);
                          // Fallback: แสดงวันที่ขาย ถ้าไม่มีวันที่ส่งมอบ
                          return schedule.sale_date ? formatDate(schedule.sale_date) : 'ไม่ระบุ';
                        })()}
                      </td>
                      <td>
                        <strong>{schedule.sale_number || 'ไม่ระบุ'}</strong>
                        <br />
                        <small className="text-muted">ID: {schedule.sale_id}</small>
                      </td>
                      <td>
                        <div>
                          {(() => {
                            // Prefer exact sale_items info by serial (from backend join), then fallback to display_* fields
                            const rawTypeKey = (schedule.item_product_type || schedule.display_product_type || '').toString().trim();
                            const type = rawTypeKey === 'battery' ? 'แบตเตอรี่' : rawTypeKey === 'charger' ? 'เครื่องชาร์จ' : (rawTypeKey || 'ไม่ระบุ');
                            const name = (schedule.product_name || schedule.display_product_name || '').toString().trim();
                            const showName = name && name !== type;

                            // Tooltip for warranty
                            const warrantyMonths = schedule.item_warranty_period;
                            const warrantyExpiry = schedule.item_warranty_expiry;
                            const tooltipParts = [];
                            if (warrantyMonths) tooltipParts.push(`ประกัน: ${warrantyMonths} เดือน`);
                            if (warrantyExpiry) tooltipParts.push(`หมดประกัน: ${formatDate(warrantyExpiry)}`);
                            const tooltip = tooltipParts.join('\n');

                            return (
                              <div>
                                <div className="fw-bold thai-text" title={tooltip || undefined}>
                                  {showName ? name : type}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                      <td>
                        <strong>{schedule.serial_number || 'ไม่ระบุ'}</strong>
                      </td>
                      <td>{schedule.customer_name || schedule.pm_customer_name || schedule.customer_name_from_sale || 'ไม่ระบุ'}</td>
                      <td>{schedule.customer_phone || schedule.pm_customer_phone || schedule.customer_phone_from_customers || schedule.quotation_phone || 'ไม่ระบุ'}</td>
                      <td>
                        {schedule.last_inspection_date
                          ? formatDate(schedule.last_inspection_date)
                          : 'ยังไม่เคยตรวจสภาพ'
                        }
                      </td>
                      <td>{formatDate(schedule.next_inspection_date)}</td>
                      <td>{getStatusBadge(
                        schedule.status,
                        schedule.inspection_count,
                        schedule.total_required || schedule.pm_service_count || 0,
                        schedule.overall_status || null
                      )}</td>
                      <td>
                        <div className="btn-group" role="group">
                          {/* ปุ่มเปิดฟอร์มตรวจสภาพ - แสดงเฉพาะเมื่อสถานะเป็น กำหนดการ หรือ กำลังดำเนินการ */}
                          {(schedule.status === 'pending' || schedule.status === 'scheduled' || schedule.status === 'in_progress') && (
                            <button
                              className="btn btn-sm btn-primary"
                              title="เปิดฟอร์มตรวจสภาพ"
                              onClick={() => handleOpenInspectionModal(schedule)}
                            >
                              <i className="fas fa-clipboard-check"></i>
                            </button>
                          )}

                          {/* ปุ่มดูประวัติการตรวจสภาพ - แสดงเสมอ */}
                          <button
                            className="btn btn-sm btn-secondary"
                            title="ดูประวัติการตรวจสภาพ"
                            onClick={() => handleOpenHistoryModal(schedule.serial_number)}
                          >
                            <i className="fas fa-history"></i>
                          </button>

                          {/* ปุ่มดาวน์โหลด PDF - แสดงเฉพาะเมื่อสถานะเป็น เสร็จสิ้น */}
                          {schedule.status === 'completed' && (
                            <button
                              className="btn btn-sm btn-success"
                              title="ดาวน์โหลด PDF (เลือกภาษา)"
                              onClick={() => handleOpenPDFLanguageModal(schedule.serial_number)}
                            >
                              <i className="fas fa-file-pdf"></i>
                            </button>
                          )}

                          {/* ปุ่มลบข้อมูล - แสดงเสมอ */}
                          <button
                            className="btn btn-sm btn-danger"
                            title="ลบข้อมูล"
                            onClick={() => handleDeleteSchedule(schedule)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center thai-text py-4">
                      <i className="fas fa-inbox fa-2x text-muted mb-2"></i>
                      <br />
                      ไม่พบข้อมูลงานตรวจสภาพ
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>



      {/* Modal ฟอร์มตรวจสภาพ */}
      {showInspectionModal && selectedSchedule && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title thai-text">
                  <i className="fas fa-clipboard-check me-2"></i>
                  ฟอร์มตรวจสภาพแบตเตอรี่ - {selectedSchedule.serial_number}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseInspectionModal}></button>
              </div>
              <div className="modal-body">
                {/* ข้อมูลพื้นฐาน */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-title thai-text">ข้อมูลการขายและลูกค้า</h6>
                        <div className="row">
                          <div className="col-md-3">
                            <strong className="thai-text">หมายเลขการขาย:</strong><br />
                            {selectedSchedule.sale_number || 'ไม่ระบุ'}
                            <br />
                            <small className="text-muted">ID: {selectedSchedule.sale_id}</small>
                          </div>
                          <div className="col-md-3">
                            <strong className="thai-text">หมายเลขซีเรียล:</strong><br />
                            {selectedSchedule.serial_number}
                            <br />
                            <small className="text-muted">{selectedSchedule.product_name || selectedSchedule.item_product_type || 'ไม่ระบุ'}</small>
                          </div>
                          <div className="col-md-3">
                            <strong className="thai-text">ชื่อลูกค้า:</strong><br />
                            {selectedSchedule.customer_name}
                          </div>
                          <div className="col-md-3">
                            <strong className="thai-text">เบอร์โทร:</strong><br />
                            {selectedSchedule.customer_phone}
                          </div>
                        </div>
                        <div className="row mt-2">
                          <div className="col-12">
                            <strong className="thai-text">ที่อยู่:</strong><br />
                            {selectedSchedule.customer_address}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ฟอร์มตรวจสภาพ */}
                <form id="inspectionForm" onSubmit={handleInspectionSubmit}>
                  <div className="row">
                    {/* ข้อมูลการตรวจสภาพ */}
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="mb-0 thai-text">ข้อมูลการตรวจสภาพ</h6>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="form-label thai-text">วันที่ตรวจสภาพ</label>
                            <input
                              type="date"
                              className="form-control"
                              name="inspection_date"
                              defaultValue={new Date().toISOString().split('T')[0]}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label thai-text">สถานที่ตรวจสภาพ</label>
                            <input
                              type="text"
                              className="form-control"
                              name="inspection_location"
                              placeholder="ระบุสถานที่ตรวจสภาพ"
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label thai-text">หมายเลขรถ</label>
                            <input
                              type="text"
                              className="form-control"
                              name="vehicle_registration"
                              placeholder="ระบุหมายเลขรถ"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label thai-text">ชื่อช่างเทคนิค</label>
                            <input
                              type="text"
                              className="form-control"
                              name="technician_name"
                              value={currentUser ? currentUser.name : ''}
                              placeholder="ระบุชื่อช่างเทคนิค"
                              readOnly
                              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                            />
                            <small className="text-muted">
                              <i className="fas fa-info-circle me-1"></i>
                              ชื่อช่างเทคนิคจะถูกดึงจากผู้ใช้ที่เข้าสู่ระบบ
                              {currentUser && currentUser.position && (
                                <span className="ms-2">
                                  <i className="fas fa-user-tag me-1"></i>
                                  ตำแหน่ง: {currentUser.position}
                                </span>
                              )}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ข้อมูลทางเทคนิค */}
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="mb-0 thai-text">ข้อมูลทางเทคนิค</h6>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="form-label thai-text">Total Volt (50-55V)</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              name="total_voltage"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label thai-text">Volt Diff (~0.1)</label>
                            <input
                              type="number"
                              step="0.001"
                              className="form-control"
                              name="voltage_difference"
                              placeholder="0.000"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label thai-text">Cycle Count (~300)</label>
                            <input
                              type="number"
                              className="form-control"
                              name="charge_cycles"
                              placeholder="0"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* งานบำรุงรักษา */}
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="mb-0 thai-text">งานบำรุงรักษา</h6>
                        </div>
                        <div className="card-body">
                          {/* แถวที่ 1: แบตเตอรี่ */}
                          <div className="mb-4">
                            <h6 className="text-primary mb-3 thai-text">
                              <i className="fas fa-battery-three-quarters me-2"></i>
                              แบตเตอรี่
                            </h6>
                            <div className="row">
                              <div className="col-md-3">
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" id="cleaning" name="cleaning" />
                                  <label className="form-check-label thai-text" htmlFor="cleaning">
                                    ทำความสะอาดภายนอก
                                  </label>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" id="terminal_cleaning" name="terminal_cleaning" />
                                  <label className="form-check-label thai-text" htmlFor="terminal_cleaning">
                                    ทำความสะอาดเทอมินอล
                                  </label>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" id="terminal_tightness" name="terminal_tightness" />
                                  <label className="form-check-label thai-text" htmlFor="terminal_tightness">
                                    ขันแน่นเทอมินอล (10Nm)
                                  </label>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" id="coulomb" name="coulomb" />
                                  <label className="form-check-label thai-text" htmlFor="coulomb">
                                    การแสดงผลคูลอมป์
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* แถวที่ 2: ชาร์จเจอร์ */}
                          <div>
                            <h6 className="text-success mb-3 thai-text">
                              <i className="fas fa-plug me-2"></i>
                              ชาร์จเจอร์
                            </h6>
                            <div className="row">
                              <div className="col-md-3">
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" id="charging" name="charging" />
                                  <label className="form-check-label thai-text" htmlFor="charging">
                                    ชาร์จเข้า (14-15A)
                                  </label>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" id="charger_status_display" name="charger_status_display" />
                                  <label className="form-check-label thai-text" htmlFor="charger_status_display">
                                    ไฟแสดงสถานะ
                                  </label>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" id="charger_cooling_fan" name="charger_cooling_fan" />
                                  <label className="form-check-label thai-text" htmlFor="charger_cooling_fan">
                                    พัดลมระบายความร้อน
                                  </label>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="form-check">
                                  <input className="form-check-input" type="checkbox" id="charger_cable_condition" name="charger_cable_condition" />
                                  <label className="form-check-label thai-text" htmlFor="charger_cable_condition">
                                    สายไฟไม่ชำรุด
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* หมายเหตุและลายเซ็น */}
                  <div className="row mt-3">
                    <div className="col-md-8">
                      <div className="mb-3">
                        <label className="form-label thai-text">หมายเหตุ</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          name="notes"
                          placeholder="ระบุหมายเหตุเพิ่มเติม..."
                        ></textarea>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label thai-text">สถานะการตรวจสภาพ</label>
                        <select className="form-select" name="status" required>
                          <option value="in_progress">กำลังดำเนินการ</option>
                          <option value="completed">เสร็จสิ้น</option>
                        </select>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="customerSignature" name="customerSignature" />
                        <label className="form-check-label thai-text" htmlFor="customerSignature">
                          ลูกค้าลงลายเซ็นแล้ว
                        </label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseInspectionModal}>
                  <i className="fas fa-times me-2"></i>
                  <span className="thai-text">ยกเลิก</span>
                </button>
                <button type="submit" className="btn btn-primary" form="inspectionForm">
                  <i className="fas fa-save me-2"></i>
                  <span className="thai-text">บันทึกข้อมูล</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal ยืนยันการลบ */}
      {showDeleteModal && scheduleToDelete && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title thai-text">
                  <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                  ยืนยันการลบข้อมูล
                </h5>
                <button type="button" className="btn-close" onClick={cancelDeleteSchedule}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong className="thai-text">คำเตือน:</strong> การลบข้อมูลนี้ไม่สามารถยกเลิกได้
                </div>
                <p className="thai-text mb-3">
                  คุณต้องการลบข้อมูลงานตรวจสภาพนี้หรือไม่?
                </p>
                <div className="card bg-light">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <strong className="thai-text">หมายเลขซีเรียล:</strong><br />
                        <span className="text-primary">{scheduleToDelete.serial_number}</span>
                      </div>
                      <div className="col-md-6">
                        <strong className="thai-text">ชื่อลูกค้า:</strong><br />
                        <span>{scheduleToDelete.customer_name || scheduleToDelete.customer_name_from_sale || 'ไม่ระบุ'}</span>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <strong className="thai-text">หมายเลขการขาย:</strong><br />
                        <span>{scheduleToDelete.sale_number || 'ไม่ระบุ'}</span>
                      </div>
                      <div className="col-md-6">
                        <strong className="thai-text">วันที่กำหนด:</strong><br />
                        <span>{formatDate(scheduleToDelete.next_inspection_date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={cancelDeleteSchedule}>
                  <i className="fas fa-times me-2"></i>
                  <span className="thai-text">ยกเลิก</span>
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDeleteSchedule} disabled={loading}>
                  <i className="fas fa-trash me-2"></i>
                  <span className="thai-text">{loading ? 'กำลังลบ...' : 'ยืนยันการลบ'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal แสดงประวัติการตรวจสภาพ */}
      {showHistoryModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title thai-text">
                  <i className="fas fa-history me-2"></i>
                  ประวัติการตรวจสภาพ
                  {inspectionHistory && ` - ${inspectionHistory.serial_number}`}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseHistoryModal}></button>
              </div>
              <div className="modal-body">
                {historyLoading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">กำลังโหลด...</span>
                    </div>
                    <p className="mt-2 thai-text">กำลังโหลดประวัติการตรวจสภาพ...</p>
                  </div>
                ) : inspectionHistory ? (
                  <div>
                    {/* สรุปข้อมูล */}
                    <div className="row mb-4">
                      <div className="col-md-4">
                        <div className="card bg-light">
                          <div className="card-body text-center">
                            <h5 className="card-title thai-text">จำนวนครั้งที่ตรวจสภาพ</h5>
                            <h2 className="text-primary">{inspectionHistory.inspection_count}</h2>
                            <small className="text-muted thai-text">ครั้ง</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-light">
                          <div className="card-body text-center">
                            <h5 className="card-title thai-text">วันที่ตรวจสภาพครั้งล่าสุด</h5>
                            <h6 className="text-success">
                              {inspectionHistory.last_inspection_date
                                ? formatDate(inspectionHistory.last_inspection_date)
                                : 'ยังไม่เคยตรวจสภาพ'
                              }
                            </h6>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-light">
                          <div className="card-body text-center">
                            <h5 className="card-title thai-text">หมายเลขซีเรียล</h5>
                            <h6 className="text-info">{inspectionHistory.serial_number}</h6>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ตารางประวัติการตรวจสภาพ */}
                    {inspectionHistory.inspections && inspectionHistory.inspections.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th className="thai-text">วันที่ตรวจสภาพ</th>
                              <th className="thai-text">สถานที่ตรวจสภาพ</th>
                              <th className="thai-text">หมายเลขรถ</th>
                              <th className="thai-text">ช่างเทคนิค</th>
                              <th className="thai-text">สถานะ</th>
                              <th className="thai-text">Total Volt (V)</th>
                              <th className="thai-text">Volt Diff (V)</th>
                              <th className="thai-text">Cycle Count</th>
                              <th className="thai-text">งานบำรุงรักษา</th>
                              <th className="thai-text">หมายเหตุ</th>
                              <th className="thai-text">การดำเนินการ</th>
                              {currentUser && currentUser.role === 'admin' && (
                                <th className="thai-text">การจัดการ</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {inspectionHistory.inspections.map((inspection, index) => (
                              <tr key={index}>
                                <td>{formatDate(inspection.inspection_date)}</td>
                                <td>{inspection.location || 'ไม่ระบุ'}</td>
                                <td>{inspection.vehicle_number || 'ไม่ระบุ'}</td>
                                <td>{inspection.technician_name || 'ไม่ระบุ'}</td>
                                <td>
                                  <span className={`badge ${inspection.status === 'completed' ? 'bg-success' :
                                      inspection.status === 'in_progress' ? 'bg-warning' :
                                        inspection.status === 'scheduled' ? 'bg-info' :
                                          inspection.status === null || inspection.status === undefined ? 'bg-secondary' : 'bg-secondary'
                                    }`}>
                                    {inspection.status === 'completed' ? 'เสร็จสิ้น' :
                                      inspection.status === 'in_progress' ? 'กำลังดำเนินการ' :
                                        inspection.status === 'scheduled' ? 'กำหนดการ' :
                                          inspection.status === null || inspection.status === undefined ? 'ไม่ระบุสถานะ' : 'ยกเลิก'}
                                  </span>
                                </td>
                                <td>{inspection.total_volt ? `${inspection.total_volt} V` : 'ไม่ระบุ'}</td>
                                <td>{inspection.v_diff ? `${inspection.v_diff} V` : 'ไม่ระบุ'}</td>
                                <td>{inspection.cycle_count || 'ไม่ระบุ'}</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-1">
                                    {!!inspection.cleaning_done && <span className="badge bg-success"> ทำความสะอาดภายนอก</span>}
                                    {!!inspection.terminal_cleaning && <span className="badge bg-success"> ทำความสะอาดเทอมินอล</span>}
                                    {!!inspection.terminal_tightness && <span className="badge bg-success"> ขันแน่นเทอมินอล (10Nm)</span>}
                                    {!!inspection.coulomb_check && <span className="badge bg-success"> การแสดงผลคูลอมป์</span>}
                                    {!!inspection.charging_done && <span className="badge bg-success"> ชาร์จเข้า (14-15A)</span>}
                                    {!!inspection.charger_status_display && <span className="badge bg-info"> ไฟแสดงสถานะ</span>}
                                    {!!inspection.charger_cooling_fan && <span className="badge bg-info"> พัดลมระบายความร้อน</span>}
                                    {!!inspection.charger_cable_condition && <span className="badge bg-info"> สายไฟไม่ชำรุด</span>}
                                    {(!inspection.cleaning_done && !inspection.terminal_cleaning &&
                                      !inspection.terminal_tightness && !inspection.coulomb_check &&
                                      !inspection.charging_done && !inspection.charger_status_display &&
                                      !inspection.charger_cooling_fan && !inspection.charger_cable_condition) &&
                                      <span className="badge bg-light text-dark">ยังไม่ได้ดำเนินการ</span>}
                                  </div>
                                </td>
                                <td>{inspection.notes || 'ไม่มี'}</td>
                                <td>
                                  {inspection.status === 'completed' && (
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => handleOpenSpecificInspectionPDF(inspection)}
                                      title="ดาวน์โหลด PDF (เลือกภาษา)"
                                    >
                                      <i className="fas fa-file-pdf me-1"></i>
                                      PDF
                                    </button>
                                  )}
                                </td>
                                {currentUser && currentUser.role === 'admin' && (
                                  <td>
                                    <button
                                      className="btn btn-sm btn-outline-primary me-2"
                                      onClick={() => handleOpenEditInspection(inspection)}
                                      title="แก้ไขประวัติการตรวจสภาพ"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteInspection(inspection)}
                                      title="ลบประวัติการตรวจสภาพ"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted thai-text">ยังไม่มีประวัติการตรวจสภาพ</h5>
                        <p className="text-muted thai-text">ยังไม่เคยมีการตรวจสภาพสำหรับหมายเลขซีเรียลนี้</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                    <h5 className="text-warning thai-text">ไม่สามารถโหลดข้อมูลได้</h5>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseHistoryModal}>
                  <i className="fas fa-times me-2"></i>
                  <span className="thai-text">ปิด</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal ยืนยันการลบประวัติการตรวจสภาพ */}
      {showDeleteInspectionModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title thai-text">
                  <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                  ยืนยันการลบประวัติการตรวจสภาพ
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteInspectionModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>คำเตือน:</strong> การลบประวัติการตรวจสภาพนี้จะไม่สามารถกู้คืนได้
                </div>
                {inspectionToDelete && (
                  <div>
                    <p className="thai-text">คุณต้องการลบประวัติการตรวจสภาพนี้หรือไม่?</p>
                    <div className="bg-light p-3 rounded">
                      <div className="row">
                        <div className="col-md-6">
                          <strong>วันที่ตรวจสภาพ:</strong><br />
                          {new Date(inspectionToDelete.inspection_date).toLocaleDateString('th-TH')}
                        </div>
                        <div className="col-md-6">
                          <strong>ช่างเทคนิค:</strong><br />
                          {inspectionToDelete.technician_name || 'ไม่ระบุ'}
                        </div>
                        <div className="col-md-6 mt-2">
                          <strong>สถานที่:</strong><br />
                          {inspectionToDelete.location || 'ไม่ระบุ'}
                        </div>
                        <div className="col-md-6 mt-2">
                          <strong>หมายเลขรถ:</strong><br />
                          {inspectionToDelete.vehicle_number || 'ไม่ระบุ'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteInspectionModal(false)}
                  disabled={deleteLoading}
                >
                  <i className="fas fa-times me-2"></i>
                  <span className="thai-text">ยกเลิก</span>
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDeleteInspection}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      <span className="thai-text">กำลังลบ...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      <span className="thai-text">ลบประวัติการตรวจสภาพ</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PM Schedule Modal */}
      {showPMScheduleModal && (
        <PMScheduleModal
          isOpen={showPMScheduleModal}
          onClose={() => {
            setShowPMScheduleModal(false);
            setSelectedDeviceForPM(null);
          }}
          deviceData={selectedDeviceForPM}
          onSuccess={() => {
            setShowPMScheduleModal(false);
            setSelectedDeviceForPM(null);
            fetchSchedules(); // Refresh the schedules list
            setCalendarRefreshTrigger(prev => prev + 1); // Refresh calendar
          }}
        />
      )}

      {/* Calendar Modal */}
      <CalendarModal
        isOpen={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
        currentUser={currentUser}
        initialDate={calendarInitialDate}
        initialView={calendarInitialView}
        refreshTrigger={calendarRefreshTrigger}
      />

      {/* PM Date Edit Modal (เปิดหลังบันทึกตรวจสภาพ) */}
      {showPMDateEditModal && pmDateEditData && (
        <PMDateEditModal
          isOpen={showPMDateEditModal}
          pmData={pmDateEditData}
          onClose={() => {
            setShowPMDateEditModal(false);
            setPmDateEditData(null);
          }}
          onSave={handleSavePMDateFromModal}
        />
      )}

      {/* Modal เลือกภาษา PDF */}
      {showPDFLanguageModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title thai-text">
                  <i className="fas fa-file-pdf text-success me-2"></i>
                  เลือกภาษาสำหรับ PDF Report
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowPDFLanguageModal(false);
                  setSelectedSerialForPDF(null);
                }}></button>
              </div>
              <div className="modal-body text-center">
                <p className="thai-text mb-4">
                  กรุณาเลือกภาษาที่ต้องการสำหรับรายงานการตรวจสภาพ
                </p>
                <p className="text-muted mb-4">
                  <strong>Serial Number:</strong> {selectedSerialForPDF}
                </p>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <button
                      className="btn btn-lg btn-outline-primary w-100"
                      onClick={() => handleDownloadPDF(selectedSerialForPDF, 'th')}
                      style={{ height: '120px' }}
                    >
                      <div>
                        <i className="fas fa-flag fa-2x mb-2"></i>
                        <br />
                        <strong>ภาษาไทย</strong>
                        <br />
                        <small>Thai Language</small>
                      </div>
                    </button>
                  </div>
                  <div className="col-md-6 mb-3">
                    <button
                      className="btn btn-lg btn-outline-success w-100"
                      onClick={() => handleDownloadPDF(selectedSerialForPDF, 'en')}
                      style={{ height: '120px' }}
                    >
                      <div>
                        <i className="fas fa-globe fa-2x mb-2"></i>
                        <br />
                        <strong>English</strong>
                        <br />
                        <small>ภาษาอังกฤษ</small>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowPDFLanguageModal(false);
                  setSelectedSerialForPDF(null);
                }}>
                  <i className="fas fa-times me-2"></i>
                  <span className="thai-text">ยกเลิก</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
